import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  formatCompanyNumber,
  parseCompanyNumber,
  parseRcsNumber,
  vatOptional,
  type PortalMeDto,
} from '@wally-reseller/shared';
import { Button } from '@wally-reseller/ui/button';
import { Input } from '@wally-reseller/ui/input';
import { cn } from '@wally-reseller/ui/utils';
import { Info } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { api, ApiError } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { useMe, useSwitchEntity } from './Layout';
import { draftToInput, emptyDraft, Field, RegistrationFields } from './RegistrationFields';

export function RegisterPage() {
  const me = useMe();
  // Rendered inside the Layout, which waits for `me`.
  return me.data ? <RegisterForm me={me.data} /> : null;
}

function RegisterForm({ me }: { me: PortalMeDto }) {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const switchEntity = useSwitchEntity();
  const several = me.entities.length > 1;
  // Someone working for several entities says which one the client is for: it is paid the
  // commission. No default, so it is a choice.
  const [entity, setEntity] = useState<string | null>(several ? null : me.partner.id);
  const [country, setCountry] = useState(several ? '' : me.partner.country);
  const [countryTouched, setCountryTouched] = useState(false);
  const [number, setNumber] = useState('');
  const [rcs, setRcs] = useState('');
  const [touched, setTouched] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);

  const optional = vatOptional(country);
  const numberValid = optional && !number.trim() ? true : !!parseCompanyNumber(country, number);
  const rcsValid = !optional || !rcs.trim() || !!parseRcsNumber(rcs);
  const valid = !!entity && numberValid && rcsValid;

  const register = useMutation({
    mutationFn: async () => {
      // The session is shared with other tabs, which may have switched it: always move it to the
      // chosen entity, and the server refuses the registration if it is elsewhere by then.
      if (several && entity) await switchEntity(entity);
      return api.register({
        ...draftToInput(draft),
        companyNumber: { country, number, rcs: optional ? rcs : '' },
        ...(entity ? { forPartnerId: entity } : {}),
      });
    },
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['registrations'] });
      queryClient.invalidateQueries({ queryKey: ['registration-counts'] });
      navigate(`/leads/${r.id}`);
    },
  });

  const chooseEntity = (id: string) => {
    setEntity(id);
    // The client is most often in the entity's country.
    const chosen = me.entities.find((e) => e.partnerId === id);
    if (chosen && !countryTouched) setCountry(chosen.country);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (valid) register.mutate();
  };

  const error = register.error;
  return (
    <form onSubmit={submit} className="max-w-2xl space-y-5">
      <h1 className="text-xl font-semibold">{t('portal.register.title')}</h1>
      <div className="flex gap-2 rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
        <Info className="mt-0.5 size-4 shrink-0 text-accent" />
        <p>{t('portal.register.rules')}</p>
      </div>
      {several && (
        <fieldset className="space-y-2 rounded-lg border border-border bg-background p-4">
          <legend className="px-1 text-sm font-medium">{t('portal.field.entity')}</legend>
          <p className="text-xs text-muted-foreground">{t('portal.field.entity.hint')}</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {me.entities.map((e) => (
              <label
                key={e.partnerId}
                className={cn(
                  'flex cursor-pointer items-start gap-2.5 rounded-md border p-3 text-sm',
                  entity === e.partnerId
                    ? 'border-accent bg-accent-soft'
                    : 'border-border hover:bg-muted',
                )}
              >
                <input
                  type="radio"
                  name="entity"
                  required
                  className="mt-0.5 accent-accent"
                  checked={entity === e.partnerId}
                  onChange={() => chooseEntity(e.partnerId)}
                />
                <span className="min-w-0">
                  <span className="block font-medium">{e.legalName}</span>
                  <span className="block text-xs text-muted-foreground">
                    {formatCompanyNumber(e.companyNumber)}
                  </span>
                </span>
              </label>
            ))}
          </div>
          {touched && !entity && (
            <div className="text-xs text-danger">{t('portal.field.entity.required')}</div>
          )}
        </fieldset>
      )}
      <div className="space-y-4 rounded-lg border border-border bg-background p-4">
        <div
          className={cn(
            'grid gap-3',
            optional ? 'sm:grid-cols-[5rem_1fr_1fr]' : 'grid-cols-[5rem_1fr]',
          )}
        >
          <Field id="country" label={t('portal.field.country')}>
            <Input
              id="country"
              required
              maxLength={2}
              value={country}
              onChange={(e) => {
                setCountryTouched(true);
                setCountry(e.target.value.toUpperCase());
              }}
            />
          </Field>
          <Field
            id="number"
            label={t(optional ? 'portal.field.vatNumber.optional' : 'portal.field.companyNumber')}
            error={touched && !numberValid ? t('portal.field.companyNumber.invalid') : null}
          >
            <Input
              id="number"
              required={!optional}
              inputMode={country === 'BE' ? 'numeric' : undefined}
              placeholder={country === 'BE' ? '0123.456.789' : country === 'LU' ? 'LU12345678' : ''}
              value={number}
              onBlur={() => setTouched(true)}
              onChange={(e) => setNumber(e.target.value)}
            />
          </Field>
          {optional && (
            <Field
              id="rcs"
              label={t('portal.field.rcs')}
              hint={t('portal.field.rcs.hint')}
              error={touched && !rcsValid ? t('portal.field.rcs.invalid') : null}
            >
              <Input
                id="rcs"
                placeholder="B123456"
                value={rcs}
                onBlur={() => setTouched(true)}
                onChange={(e) => setRcs(e.target.value)}
              />
            </Field>
          )}
        </div>
        <RegistrationFields draft={draft} onChange={setDraft} />
      </div>
      {error && (
        <p className="text-sm text-danger">
          {error instanceof ApiError && error.status === 409
            ? t('portal.register.duplicate')
            : error instanceof ApiError && error.status === 412
              ? t('portal.register.entityChanged')
              : t('portal.error.generic')}
        </p>
      )}
      <Button type="submit" size="lg" disabled={register.isPending}>
        {t('portal.register.submit')}
      </Button>
    </form>
  );
}
